import {FC, MouseEventHandler, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import SidebarIcon from "../../assets/icons/SidebarIcon";
import TopFXSmallLogo from "../../assets/icons/TopFXSmallLogo";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {isDarkMode} from "../../App";
import {ENV} from "../../pages/admin/Login";
import TotalFXSmallLogo from "../../assets/icons/TotalFXSmallLogo";

interface SidebarIconTooltipProps {
    text: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
    className?: string;
    iconClassName?: string;
    withLogo?: boolean
}

const SidebarIconTooltip: FC<SidebarIconTooltipProps> = ({
                                                             text,
                                                             onClick,
                                                             className = '',
                                                             iconClassName = '',
                                                             withLogo = true
                                                         }) => {
    const isSidebarMinimized = useSelector((state: RootState) => state.sidebar.isMini);
    const [isHovered, setIsHovered] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const pos = useMemo(() => {
        if (!isHovered || !anchorRef.current) return null;
        const r = anchorRef.current.getBoundingClientRect();
        return {
            top: r.top + r.height / 2,
            left: r.right + 8, // ml-2
        };
    }, [isHovered]);

    const getLogoUI = useMemo(() => {
        const env = process.env as unknown as ENV;
        if (env.REACT_APP_LOGO === 'totalfx')
            return <TotalFXSmallLogo
                className="w-[2.5rem] h-[2.5rem]"
                secondaryColor={isDarkMode ? 'lightgray' : '#041E68'}
            />
        return <TopFXSmallLogo
            className="w-[2.5rem] h-[2.5rem]"
            primaryColor={isDarkMode ? 'lightgray' : 'black'}
            secondaryColor={isDarkMode ? 'lightgray' : '#444'}
        />
    }, []);

    return (
        <div
            ref={anchorRef}
            className={`relative inline-flex items-center md:w-full ${className}`}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
        >
            {isSidebarMinimized && withLogo && !isHovered ? getLogoUI :
                <SidebarIcon className={`w-[1.5rem] ${isSidebarMinimized ? 'h-[2.5rem]' : ''} ${iconClassName}`}/>
            }

            {isHovered && pos && createPortal(
                <div
                    className="pointer-events-none fixed z-[99999] whitespace-nowrap rounded bg-blue-gray-900 text-white text-xs py-1 px-4 shadow-lg"
                    style={{
                        top: pos.top,
                        left: pos.left,
                        transform: 'translateY(-50%)',
                    }}
                >
                    {text}
                </div>,
                document.body
            )}
        </div>
    );
};

export default SidebarIconTooltip;
